import { RotateCw, Download, LayoutGrid, Lock } from 'lucide-react';
import { Button, Tooltip, cn } from '../../ui';
import { TONE, categoryMeta, type ReportDefinition } from './reportKit';

/**
 * The workspace header — who you are looking at, and the two things you do to it.
 *
 * It replaces a 110px card with a category chip, a two-line description, a pill row and a gradient.
 * All of that said one thing the reader already knew (which report they opened) and pushed the rows
 * that far down every single time. What survives is the breadcrumb (which is also the way back), the
 * name, one line of description, and the actions — Refresh and Export — which stay in reach at the top of
 * the workspace instead of scrolling away above the table.
 */
export function ReportWorkspaceHeader({
  report, onHome, onArea, onChangeReport, onRun, running, canRun, onExport, exporting, canExport,
}: {
  report: ReportDefinition;
  onHome: () => void;
  onArea: (area: string) => void;
  onChangeReport: () => void;
  onRun: () => void;
  running: boolean;
  canRun: boolean;
  /** Opens the export dialog — the one place PDF, Excel and CSV are offered. */
  onExport: () => void;
  exporting: boolean;
  /**
   * Does this reader hold `REPORT.EXPORT`? Mirrors the server's gate on the six export routes, so the
   * dialog never offers a file the API will refuse. Reading the report is unaffected — that is
   * `REPORT.RUN`, which they clearly have, or this screen would not have opened.
   */
  canExport: boolean;
}) {
  const m = categoryMeta(report.category);
  const tone = TONE[m.tone];

  return (
    <header className="border-b border-line pb-2.5">
      {/* Breadcrumb: where this report sits, and both levels are the way back out. */}
      <nav aria-label="Breadcrumb" className="flex min-w-0 items-center gap-1 type-caption text-subtle">
        <button type="button" onClick={onHome} className="rounded px-1 py-0.5 text-muted transition-colors hover:bg-surface-2 hover:text-content">
          Reports
        </button>
        <span aria-hidden>/</span>
        <button
          type="button"
          onClick={() => onArea(report.category)}
          className={cn('inline-flex items-center gap-1 rounded px-1 py-0.5 transition-colors hover:bg-surface-2', tone.ink)}
        >
          <span className={cn('size-1.5 rounded-pill', tone.dot)} aria-hidden />
          {report.category}
        </button>
        <span aria-hidden>/</span>
        <span className="min-w-0 truncate text-content">{report.name}</span>
        {report.scope === 'global' && (
          <Tooltip title="Whole-company figures. Only Admin and Accounts can run this one.">
            <span className="ml-1 inline-flex shrink-0 items-center gap-1 rounded-pill border border-line px-1.5 py-px text-subtle">
              <Lock className="size-3" />company-wide
            </span>
          </Tooltip>
        )}
      </nav>

      <div className="mt-1 flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
        <div className="min-w-0 flex-1">
          <h1 className="truncate type-section-title text-content" title={report.name}>{report.name}</h1>
          {/* One line. The full text is a hover away rather than three lines of the viewport. */}
          <p className="mt-0.5 line-clamp-1 text-sm text-muted" title={report.description}>{report.description}</p>
        </div>

        <div className="flex shrink-0 flex-wrap items-center gap-2">
          <Button variant="default" size="sm" icon={<LayoutGrid size={15} />} onClick={onChangeReport}>
            <span className="hidden sm:inline">Change report</span>
            <span className="sm:hidden">Reports</span>
          </Button>

          {/* Straight to the export dialog. There used to be a menu of PDF / Excel / CSV AND an
              "Export options…" item opening a dialog with the same three again — two lists of one
              choice. The dialog is the one kept, because it restates the period, filters and row count
              before a file is written, which is what makes an exported file traceable.

              Without REPORT.EXPORT the button stays visible but disabled, and says why. Removing it
              outright would leave a reader who used to export this report with nothing to read —
              "the button is gone" is not an explanation, and they would file a bug instead of asking
              for the permission. */}
          {canExport ? (
            <Button variant="default" size="sm" icon={<Download size={15} />} disabled={!canRun} loading={exporting} onClick={onExport}>
              Export
            </Button>
          ) : (
            <Tooltip label="You don't have permission to export reports (requires REPORT.EXPORT). Reading this one on screen is unaffected.">
              <span className="inline-block cursor-not-allowed">
                <Button variant="default" size="sm" icon={<Download size={15} />} disabled style={{ pointerEvents: 'none' }}>
                  Export
                </Button>
              </span>
            </Tooltip>
          )}

          {/* The report runs on its own when it opens and when a parameter changes; this only re-reads
              the same parameters, for figures that have moved since. */}
          <Button variant="default" size="sm" icon={<RotateCw size={15} />} disabled={!canRun} onClick={onRun} loading={running}>
            Refresh
          </Button>
        </div>
      </div>
    </header>
  );
}
